import React, { useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  AlertOctagon,
  Inbox,
  ArrowRight,
  MailOpen,
  MousePointerClick,
  ReplyAll,
  Star,
} from "lucide-react";
import { PaperPlane } from "@phosphor-icons/react";
import { SenderStats } from "@/types/statistics.types";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { DashboardSummary } from "@/types/dashboard.types";

type Props = {
  senderStats: {
    summary: DashboardSummary;
  };
};

type SummaryCard = {
  id: string;
  title: string;
  value: keyof DashboardSummary;
  icon: React.ComponentType<{ className?: string }>;
};

const initialSummaryCards: SummaryCard[] = [
  {
    id: "1",
    title: "Total Warmup Email Sent",
    value: "totalWarmupEmailsSent",
    icon: PaperPlane,
  },
  {
    id: "2",
    title: "Total Emails Received in Inbox",
    value: "totalInboxEmails",
    icon: Inbox,
  },
  {
    id: "3",
    title: "Total Email Moved Out of Spam",
    value: "totalunspam",
    icon: ArrowRight,
  },
  { id: "4", title: "Total Warmup Email Opens", value: "totalOpens", icon: MailOpen },
  { id: "5", title: "Total Stars", value: "totalStars", icon: Star },
  { id: "6", title: "Total Replies", value: "totalReplies", icon: ReplyAll },
  {
    id: "7",
    title: "Total Clicks",
    value: "totalClicks",
    icon: MousePointerClick,
  },
  {
    id: "8",
    title: "Total Email Received in Spam",
    value: "totalSpamEmails",
    icon: AlertOctagon,
  },
];

// Draggable Card Component
const DraggableCard = ({
  card,
  index,
  moveCard,
  senderStats,
}: {
  card: SummaryCard;
  index: number;
  moveCard: (fromIndex: number, toIndex: number) => void;
  senderStats: {
    summary: DashboardSummary;
  };
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const [{ isDragging }, drag] = useDrag(() => ({
    type: "CARD",
    item: { id: card.id, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  const [{ isOver }, drop] = useDrop(() => ({
    accept: "CARD",
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
    drop: (item: { id: string; index: number }) => {
      if (item.index !== index) {
        moveCard(item.index, index);
      }
    },
    hover: (item: { id: string; index: number }, monitor) => {
      if (!ref.current || item.index === index) return;

      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;

      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      if (item.index < index && hoverClientY < hoverMiddleY) return;
      if (item.index > index && hoverClientY > hoverMiddleY) return;

      moveCard(item.index, index);
    },
  }));

  drag(drop(ref));

  return (
    <div
      ref={ref}
      className={`transition-all duration-200 ease-out ${
        isOver ? 'scale-105' : ''
      } ${isDragging ? 'opacity-50 scale-95' : 'opacity-100'}`}
    >
      <Card className="p-4 space-y-2 col-span-1 shadow-none hover:shadow-md transition-shadow h-full">
        <CardHeader className="p-0">
          <CardTitle className="text-sm font-medium">
            <div className="flex lg:items-center gap-2 flex-col lg:flex-row">
              <div className="border rounded-lg bg-secondary size-8 flex items-center justify-center shrink-0">
                <card.icon className="size-4 shrink-0" />
              </div>
              {card.title}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="text-2xl font-bold">
            {card.value === "totalWarmupEmailsSent"
              ? Math.max(
                  senderStats?.summary.totalWarmupEmailsSent ?? 0,
                  senderStats?.summary.totalOpens ?? 0
                )
              : (senderStats?.summary[card.value] ?? 0)}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const TopStatCards = ({ senderStats }: Props) => {
  const [cards, setCards] = useState<SummaryCard[]>(initialSummaryCards);

  const moveCard = (fromIndex: number, toIndex: number) => {
    const newCards = [...cards];
    const [movedCard] = newCards.splice(fromIndex, 1);
    newCards.splice(toIndex, 0, movedCard);
    setCards(newCards);
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="col-span-full grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
        {cards.map((card, index) => (
          <DraggableCard
            key={card.id}
            card={card}
            index={index}
            moveCard={moveCard}
            senderStats={senderStats}
          />
        ))}
      </div>
    </DndProvider>
  );
};

export default TopStatCards;
